import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { TextFieldWrapper } from '@Components/FormFieldsWrappers';
import { Button } from '@Components/index';
import Modal from '@Components/Modal';
import { useEditCallMonitorPercentageByIdMutation } from '@Services/settings.service';
import { BaseAlertProps } from '@Typings/global.types';
import useAlert from '@Utils/hooks/useAlert.hook';

import {
  CallMonitorPercentageEditModalInputs,
  CallMonitorPercentageEditModalProps,
  CallMonitorPercentageEditModalRequest,
} from './CallMonitorPercentageEditModal.types';

const CallMonitorPercentageEditModal: FunctionComponent<CallMonitorPercentageEditModalProps> =
  (props: CallMonitorPercentageEditModalProps): JSX.Element => {
    const { callMonitorPercentageEditData, isOpen, setIsOpen } = props;

    const { t } = useTranslation();
    const { triggerAlert } = useAlert();

    const [alertState, setAlertState] = useState<BaseAlertProps>({
      openAlert: false,
    });

    // edit call monitor percentage value
    const [editCallMonitorPercentage, editCallMonitorPercentageMethods] =
      useEditCallMonitorPercentageByIdMutation();

    const required = t('common.formErrorMessages.required');
    const percentageError = t('common.formErrorMessages.percentage_error');
    const methods = useForm<CallMonitorPercentageEditModalInputs>({
      resolver: yupResolver(
        yup.object({
          percentage: yup
            .string()
            .matches(/^(?:[1-9][0-9]?|100)$/, percentageError)
            .required(required),
        })
      ),
      mode: 'onChange',
      shouldUnregister: true,
    });

    /**
     * Edit call monitor percentage value form methods: updated values on submit
     */
    const onSubmitHandler: SubmitHandler<CallMonitorPercentageEditModalRequest> =
      (formData) => {
        if (formData) {
          return editCallMonitorPercentage({
            id: callMonitorPercentageEditData.id,
            percentage: formData.percentage,
          });
        }
      };

    /**
     * Error and Success alerts for editing call monitor percentage value
     */
    useEffect(() => {
      if (editCallMonitorPercentageMethods.error) {
        triggerAlert(editCallMonitorPercentageMethods.error, setAlertState);
      }
      if (editCallMonitorPercentageMethods.isSuccess) {
        setIsOpen(false);
      }
    }, [
      editCallMonitorPercentageMethods.isSuccess,
      editCallMonitorPercentageMethods.error,
      editCallMonitorPercentageMethods.data,
      triggerAlert,
      editCallMonitorPercentageMethods,
      setIsOpen,
    ]);

    const closeModal = () => {
      setIsOpen(false);
    };

    useEffect(() => {
      methods.reset({
        percentage: callMonitorPercentageEditData.value,
      });
    }, [methods, callMonitorPercentageEditData]);

    return (
      <>
        <Modal
          open={isOpen}
          headerText={t('adminSettings.Edit Call Monitoring')}
          onClose={() => closeModal()}
          alertState={alertState}
        >
          <FormProvider {...methods}>
            <Box
              component="form"
              onSubmit={methods.handleSubmit(onSubmitHandler)}
            >
              <>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  sx={{ padding: '3rem 5.5rem 2.4rem' }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    my={2}
                    gap={0.8}
                  >
                    <TextFieldWrapper
                      name="percentage"
                      label={`${t(
                        'common.messages.Call Monitor Percentage'
                      )} *`}
                      placeholder={t('common.messages.Call Monitor Percentage')}
                      sx={{ minWidth: '315px' }}
                    />
                  </Box>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{
                    borderTop: '0.1rem solid #A3ABB2',
                    padding: '3rem 2.4rem 2.4rem',
                  }}
                >
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      onClick={() => closeModal()}
                      color="secondary"
                      variant="outlined"
                      sx={{ ml: 'auto', mr: 1.6 }}
                    >
                      {t('common.messages.Cancel')}
                    </Button>
                    <Button type="submit" color="primary">
                      {t('common.messages.Submit')}
                    </Button>
                  </Box>
                </Box>
              </>
            </Box>
          </FormProvider>
        </Modal>
      </>
    );
  };

export default CallMonitorPercentageEditModal;
